import PropTypes from 'prop-types';
import { App, Card, Form, Input, Upload, Button, Space, Tooltip, Typography, Tag } from 'antd';
import { UploadOutlined, LockOutlined, EditOutlined } from '@ant-design/icons';
import { useAuthStore } from '@stores/auth.store';
import { updateTenantProfile } from '@services/registry';
import { replaceCloudinaryImage } from '@services/cloudinary.service';
import { useEffect, useMemo, useRef, useState } from 'react';

const SAVE_LOCKED_HINT =
    'Save is locked until you upload a new logo or change any school detail below.';

const cardShell = {
    borderRadius: 16,
    border: '1px solid rgba(15, 23, 42, 0.06)',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 10px 32px rgba(15, 23, 42, 0.08)',
};

const SchoolProfilePanel = ({ variant = 'page' }) => {
    const isDrawer = variant === 'drawer';
    const { message } = App.useApp();
    const user = useAuthStore((s) => s.user);
    const patchUser = useAuthStore((s) => s.patchUser);
    const [form] = Form.useForm();
    const schoolNameWatch = Form.useWatch('schoolName', form);

    const tenantId = user?.tenantId;
    const logoUrl = user?.logoUrl || null;
    const logoPublicId = user?.logoPublicId || null;
    const [pendingLogo, setPendingLogo] = useState(null);
    const [saving, setSaving] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const initialRef = useRef({
        schoolName: user?.schoolName || '',
        region: user?.region || '',
        phone: user?.phone || '',
        address: user?.address || '',
    });

    const [dirty, setDirty] = useState(false);

    const pendingPreviewUrl = useMemo(
        () => (pendingLogo ? URL.createObjectURL(pendingLogo) : null),
        [pendingLogo]
    );

    useEffect(() => {
        return () => {
            if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl);
        };
    }, [pendingPreviewUrl]);

    const computeDirty = (nextPendingLogo = pendingLogo) => {
        const v = form.getFieldsValue();
        const base = initialRef.current;
        const changed =
            (v.schoolName || '') !== (base.schoolName || '') ||
            (v.region || '') !== (base.region || '') ||
            (v.phone || '') !== (base.phone || '') ||
            (v.address || '') !== (base.address || '');
        setDirty(changed || !!nextPendingLogo);
    };

    const cancelEdit = () => {
        form.setFieldsValue({ ...initialRef.current });
        setPendingLogo(null);
        setEditMode(false);
        setTimeout(() => computeDirty(null), 0);
    };

    const onFinish = async (values) => {
        if (!tenantId) return;
        try {
            setSaving(true);

            let logoPatch = {};
            if (pendingLogo) {
                const res = await replaceCloudinaryImage({
                    file: pendingLogo,
                    folder: `tenants/${tenantId}/branding`,
                    previousPublicId: logoPublicId,
                });
                logoPatch = { logoUrl: res.secureUrl, logoPublicId: res.publicId };
            }

            const patch = {
                schoolName: values.schoolName || null,
                region: values.region || null,
                phone: values.phone || null,
                address: values.address || null,
                ...logoPatch,
            };
            await updateTenantProfile(tenantId, patch);
            patchUser(patch);

            initialRef.current = {
                schoolName: values.schoolName || '',
                region: values.region || '',
                phone: values.phone || '',
                address: values.address || '',
            };
            setPendingLogo(null);
            setDirty(false);
            setEditMode(false);
            message.success('Profile updated');
        } catch (e) {
            message.error(e?.message || 'Update failed');
        } finally {
            setSaving(false);
        }
    };

    const saveDisabled = saving || !dirty;
    const lockedNoChanges = !dirty && !saving;
    const saveTooltip = useMemo(() => {
        if (saving) return 'Saving…';
        if (!dirty) return SAVE_LOCKED_HINT;
        return 'Save your updates to the school profile';
    }, [dirty, saving]);

    const logoSrc = pendingPreviewUrl || logoUrl || '/edulink-mark.png';
    const displayName =
        (schoolNameWatch ?? '').trim() ||
        (initialRef.current.schoolName || '').trim() ||
        'School name';

    const editCancelControl = !editMode ? (
        <Tooltip title="Edit school details and logo" placement="left">
            <Button type="text" icon={<EditOutlined />} aria-label="Enable edit mode" onClick={() => setEditMode(true)} />
        </Tooltip>
    ) : (
        <Tooltip title="Discard changes and leave edit mode" placement="left">
            <Button type="link" size="small" onClick={cancelEdit} style={{ padding: 0, height: 'auto' }}>
                Cancel
            </Button>
        </Tooltip>
    );

    const formBlock = (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <Upload
                    accept="image/*"
                    maxCount={1}
                    showUploadList={false}
                    disabled={!editMode}
                    beforeUpload={(file) => {
                        if (!editMode) return false;
                        setPendingLogo(file);
                        setTimeout(() => computeDirty(file), 0);
                        return false;
                    }}
                >
                    <Tooltip title={!editMode ? 'Click the pencil above to enable uploads' : undefined}>
                        <span>
                            <Button icon={<UploadOutlined />} disabled={!editMode}>
                                Upload logo
                            </Button>
                        </span>
                    </Tooltip>
                </Upload>
            </div>

            <Form
                form={form}
                layout="vertical"
                initialValues={initialRef.current}
                onFinish={onFinish}
                onValuesChange={() => computeDirty()}
            >
                <Form.Item
                    label="School Name"
                    name="schoolName"
                    rules={[{ required: true, message: 'School name is required' }]}
                >
                    <Input
                        placeholder="School name"
                        readOnly={!editMode}
                        tabIndex={editMode ? 0 : -1}
                        style={!editMode ? { cursor: 'default', background: '#f5f5f5' } : undefined}
                    />
                </Form.Item>
                <Form.Item label="Region" name="region">
                    <Input
                        placeholder="Region"
                        readOnly={!editMode}
                        tabIndex={editMode ? 0 : -1}
                        style={!editMode ? { cursor: 'default', background: '#f5f5f5' } : undefined}
                    />
                </Form.Item>
                <Form.Item label="Phone" name="phone">
                    <Input
                        placeholder="Phone"
                        readOnly={!editMode}
                        tabIndex={editMode ? 0 : -1}
                        style={!editMode ? { cursor: 'default', background: '#f5f5f5' } : undefined}
                    />
                </Form.Item>
                <Form.Item label="Address" name="address">
                    <Input
                        placeholder="Address"
                        readOnly={!editMode}
                        tabIndex={editMode ? 0 : -1}
                        style={!editMode ? { cursor: 'default', background: '#f5f5f5' } : undefined}
                    />
                </Form.Item>

                {editMode && (
                    <Tooltip title={saveTooltip} placement="top">
                        <span style={{ display: 'block' }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                block
                                disabled={saveDisabled}
                                icon={lockedNoChanges ? <LockOutlined /> : undefined}
                                loading={saving}
                                style={
                                    lockedNoChanges
                                        ? {
                                              background: '#cbd5e1',
                                              borderColor: '#cbd5e1',
                                              color: '#64748b',
                                              cursor: 'not-allowed',
                                          }
                                        : undefined
                                }
                            >
                                Save
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </Form>
        </Space>
    );

    if (isDrawer) {
        const adminLine = user?.adminEmail || user?.email || '—';
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Card bordered={false} style={{ ...cardShell }} styles={{ body: { padding: 20 } }}>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                        <div
                            style={{
                                width: 88,
                                height: 88,
                                flexShrink: 0,
                                borderRadius: 14,
                                border: '1px solid rgba(15, 23, 42, 0.08)',
                                background: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                            }}
                        >
                            <img src={logoSrc} alt="School logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                <div style={{ minWidth: 0 }}>
                                    <Typography.Text type="secondary" style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                        School profile
                                    </Typography.Text>
                                    <Typography.Title level={5} style={{ margin: '4px 0 0', fontSize: 17, lineHeight: 1.35 }} ellipsis>
                                        {displayName}
                                    </Typography.Title>
                                    <Space size={8} wrap style={{ marginTop: 10 }}>
                                        <Tag color="red">{user?.plan || '—'}</Tag>
                                        <Tag>{user?.region || '—'}</Tag>
                                    </Space>
                                </div>
                                {editCancelControl}
                            </div>
                            <Typography.Paragraph type="secondary" style={{ margin: '14px 0 0', fontSize: 12, marginBottom: 0 }}>
                                {!editMode
                                    ? 'Use the pencil to edit details and upload a new logo.'
                                    : 'Changes apply after you tap Save.'}
                            </Typography.Paragraph>
                            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 10 }}>
                                Admin: <Typography.Text style={{ fontSize: 12 }}>{adminLine}</Typography.Text>
                            </Typography.Text>
                        </div>
                    </div>
                </Card>

                <Card
                    title="Contact & branding"
                    bordered={false}
                    style={{ ...cardShell }}
                    styles={{ body: { padding: '8px 20px 20px' }, header: { borderBottom: '1px solid rgba(15,23,42,0.06)', minHeight: 48 } }}
                >
                    {formBlock}
                </Card>
            </div>
        );
    }

    return (
        <div
            style={{
                maxWidth: 960,
                margin: '0 auto',
                width: '100%',
                display: 'flex',
                gap: 24,
                alignItems: 'flex-start',
                flexWrap: 'wrap',
            }}
        >
            <aside
                style={{
                    width: 280,
                    flexShrink: 0,
                    borderRadius: 16,
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    background: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
                    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
                    padding: 20,
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 8,
                        marginBottom: 16,
                    }}
                >
                    <Typography.Text strong style={{ fontSize: 15 }}>
                        School
                    </Typography.Text>
                    {editCancelControl}
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                    <img
                        src={logoSrc}
                        alt="School logo"
                        style={{
                            width: 112,
                            height: 112,
                            objectFit: 'contain',
                            borderRadius: 14,
                            border: '1px solid rgba(15,23,42,0.08)',
                            background: '#fff',
                        }}
                    />
                </div>

                <Typography.Title level={5} style={{ margin: 0, textAlign: 'center', fontSize: 16 }} ellipsis>
                    {displayName}
                </Typography.Title>
                {!editMode && (
                    <Typography.Paragraph type="secondary" style={{ margin: '12px 0 0', textAlign: 'center', fontSize: 13 }}>
                        Use the pencil to edit your profile and logo.
                    </Typography.Paragraph>
                )}
            </aside>

            <div style={{ flex: 1, minWidth: 280 }}>
                <Card
                    title="School Profile"
                    bordered={false}
                    style={{ ...cardShell }}
                    styles={{ body: { padding: 24 }, header: { borderBottom: '1px solid rgba(15,23,42,0.06)' } }}
                >
                    {formBlock}
                </Card>
            </div>
        </div>
    );
};

SchoolProfilePanel.propTypes = {
    variant: PropTypes.oneOf(['page', 'drawer']),
};

export default SchoolProfilePanel;
